import { isIOS } from 'mobile-device-detect';
import { LOCALE_KEY } from '../../Constants/LocaleKeys';
import Game from '../../Game';
import {
    GameActions,
    ResponseStatus,
    ResponseType,
} from '../../State/Game/GameActions';
import { IGameState } from '../../State/Game/IGameState';
import { HelperFriendActions } from '../../State/Level/HelperFriend/HelperFriendActions';
import { LevelActions } from '../../State/Level/LevelActions';
import { isProduct, PurchaseStatus } from '../../State/Player/Product';
import { trackPurchase } from '../../State/Player/ProductTrackingHelper';
import { Leaderboard } from '../../System/UI/LeaderboardHelper';
import { Random } from '../Random';
import { AdType } from '../Tracking/DataSet/Ad';
import { gameTracking } from '../Tracking/FBLegacyTracking';
import { FBTrackingEvent } from '../Tracking/FBTrackingEvent';
import { sendEvent, track } from '../Tracking/Track';
import { AdTrackingEventName } from '../Tracking/TrackingEvent';
import { trackPurchasedProduct } from '../Tracking/TrackingHelpers/Transaction';
import {
    AdvertisementType,
    IAdvertisementPayload,
    IAdvertisementPlacement,
} from './Advertisement';
import { IScoreEntry } from './ISDKDriver';
import PayloadHelper from './PayloadHelper';
import { PayloadType } from './PayloadType';
import { trackAdvertisement } from '../Tracking/TrackingHelpers/Ad';

export default class SDKIntegrationsHelper {
    public static async sendLife(friendId: string) {
        const base64Image = await PayloadHelper.getSendLifeBase64();

        try {
            // Attempt to switch into context.
            await Game.sdk.createContext(friendId);

            Game.sdk.customMessage(
                Game.locale.getText(LOCALE_KEY.BOT_SEND_LIFE_MESSAGE),
                base64Image,
                {
                    data: PayloadType.SEND_LIFE,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                },
                'IMMEDIATE',
                Game.locale.getText(LOCALE_KEY.BOT_SEND_LIFE_CTA)
            );

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.SEND_LIFE,
                    friendId
                )
            );
        } catch (e) {
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.SEND_LIFE,
                    e
                )
            );
        }
    }

    public static async requestLife(friendId: string) {
        const base64Image = await PayloadHelper.getRequestLifeBase64();

        try {
            // Attempt to switch into context.
            await Game.sdk.createContext(friendId);

            Game.sdk.customMessage(
                Game.locale.getText(LOCALE_KEY.BOT_REQUEST_LIFE_MESSAGE),
                base64Image,
                {
                    data: PayloadType.REQUEST_LIFE,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                },
                'IMMEDIATE',
                Game.locale.getText(LOCALE_KEY.BOT_REQUEST_LIFE_CTA)
            );

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.REQUEST_LIFE,
                    friendId
                )
            );
        } catch (e) {
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.REQUEST_LIFE,
                    e
                )
            );
        }
    }

    public static async helperFriendRequest(friendId: string) {
        const base64Image = await PayloadHelper.getHelpFriendBase64();
        try {
            Game.sdk.gameTracking(FBTrackingEvent.Helper_Accept);

            // Attempt to switch into context.
            await Game.sdk.createContext(friendId);

            const messageKey = isIOS
                ? LOCALE_KEY.BOT_HELP_FRIEND_MESSAGE
                : LOCALE_KEY.BOT_HELP_FRIEND_EXTRA_CARD_MESSAGE;
            const ctaKey = isIOS
                ? LOCALE_KEY.BOT_HELP_FRIEND_CTA
                : LOCALE_KEY.BOT_HELP_FRIEND_EXTRA_CARD_CTA;

            Game.sdk.customMessage(
                Game.locale.getText(messageKey),
                base64Image,
                {
                    data: PayloadType.HELP_FRIEND,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                },
                'IMMEDIATE',
                Game.locale.getText(ctaKey)
            );
            Game.sdk.gameTracking(FBTrackingEvent.Helper_Confirm);
            Game.state.dispatch(
                HelperFriendActions.helperFriendResponse(true, friendId)
            );
        } catch (e) {
            Game.state.dispatch(
                HelperFriendActions.helperFriendResponse(false)
            );
        }
    }

    public static async inviteFriend() {
        const base64Image = await PayloadHelper.getInviteFriendBase64();

        try {
            await Game.sdk.share(
                Game.locale.getText(LOCALE_KEY.BOT_INVITE_FRIEND_MESSAGE),
                base64Image,
                { data: PayloadType.INVITE_FRIEND }
            );
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.INVITE_FRIEND
                )
            );
        } catch (e) {
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.INVITE_FRIEND,
                    e
                )
            );
        }
    }

    public static async challengeFriend() {
        const base64Image = await PayloadHelper.getInviteFriendBase64();

        try {
            await Game.sdk.playWithFriends(
                Game.locale.getText(LOCALE_KEY.BOT_CHALLENGE_FRIEND_MESSAGE),
                base64Image,
                { data: PayloadType.CHALLENGE_FRIEND }
            );
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.PLAY_WITH_FRIENDS
                )
            );
        } catch (e) {
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.PLAY_WITH_FRIENDS,
                    e
                )
            );
        }
    }

    public static async handlePurchaseProduct(productId: string) {
        // Validate specified productId is a valid product.
        if (!isProduct(productId))
            return Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.PURCHASE_PRODUCT,
                    'Invalid Product ID'
                )
            );
        try {
            trackPurchase(productId, PurchaseStatus.START);
            await Game.sdk.purchaseProduct(productId);
            trackPurchase(productId, PurchaseStatus.SUCCESS);

            try {
                const product = Game.sdk.getCatalog().products.find(element => {
                    return element.productId === productId;
                });
                const price = parseInt(product.price.match(/\d/g).join(''), 10);
                trackPurchasedProduct(
                    price,
                    product.priceCurrencyCode.toUpperCase()
                );
            } catch (e) {
                console.log('Error on Transaction Tracking:' + e);
            }

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.PURCHASE_PRODUCT,
                    productId
                )
            );
        } catch (e) {
            trackPurchase(productId, PurchaseStatus.FAIL);
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.PURCHASE_PRODUCT,
                    e
                )
            );
        }
    }

    public static async sendGateUnlockFriendRequest() {
        const base64Image = await PayloadHelper.getGateLockedBase64();

        let contextData;
        try {
            const result = await Game.sdk.playWithFriends(
                Game.locale.getText(LOCALE_KEY.BOT_REQUEST_OPEN_GATE_MESSAGE),
                base64Image,
                {
                    userId: Game.sdk.getUserId(),
                    messageId: Random.ID(),
                    data: PayloadType.GATE_UNLOCK_ADD_FRIEND_REQUEST,
                }
            );

            contextData = {
                externalContextID: result,
            };

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.PLAY_WITH_FRIENDS,
                    contextData
                )
            );
        } catch (e) {
            contextData = {
                code: e.code,
                message: e.message,
            };

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.PLAY_WITH_FRIENDS,
                    contextData
                )
            );
        }
    }

    public static async sendGateUnlockFriendResponse(friendId: string) {
        const base64Image = await PayloadHelper.getGateUnlockedBase64();

        await Game.sdk.createContext(friendId);

        Game.sdk.customMessage(
            Game.locale.getText(LOCALE_KEY.BOT_SEND_OPEN_GATE_MESSAGE),
            base64Image,
            {
                userId: Game.sdk.getUserId(),
                userAvatar: Game.sdk.getAvatar(),
                messageId: Random.ID(),
                data: PayloadType.GATE_UNLOCK_ADD_FRIEND_RESPONSE,
            }
        );
    }

    public static async giveLife(friendId: string, friendName: string) {
        const base64Image = await PayloadHelper.getSendLifeBase64();

        await Game.sdk.createContext(friendId);

        // Should already be in context with friend - no need to switch.
        Game.sdk.customMessage(
            Game.locale.getText(LOCALE_KEY.BOT_REQUEST_LIFE_ANSWER_MESSAGE),
            base64Image,
            {
                data: PayloadType.SEND_LIFE,
                messageId: Random.ID(),
                userId: Game.sdk.getUserId(),
                userAvatar: Game.sdk.getAvatar(),
                userName: Game.sdk.getUsername(),
            }
        );
    }

    public static async sendGift(friendId: string) {
        const base64Image = await PayloadHelper.getSendGiftBase64();

        try {
            await Game.sdk.createContext(friendId);

            // Should already be in context with friend - no need to switch.
            Game.sdk.customMessage(
                Game.locale.getText(LOCALE_KEY.BOT_SEND_GIFT_MESSAGE),
                base64Image,
                {
                    data: PayloadType.SEND_GIFT,
                    messageId: Random.ID(),
                    userId: Game.sdk.getUserId(),
                    userAvatar: Game.sdk.getAvatar(),
                    userName: Game.sdk.getUsername(),
                },
                'IMMEDIATE',
                Game.locale.getText(LOCALE_KEY.BOT_SEND_GIFT_CTA)
            );

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.SEND_GIFT,
                    friendId
                )
            );
        } catch (e) {
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.SEND_GIFT,
                    e
                )
            );
        }
    }

    public static async shareBeatHighscoreLevel(
        levelNumber: number,
        friendId: string
    ) {
        const leaderboardEntries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.LEVEL_SCORE,
            levelNumber.toString()
        );

        const userId: string = Game.sdk.getUserId();

        const base64Image = await PayloadHelper.getBeatHighscoreLevelBase64(
            levelNumber,
            leaderboardEntries.find(e => e.user.playerId === userId),
            leaderboardEntries.find(e => e.user.playerId === friendId)
        );

        try {
            await Game.sdk.createContext(friendId);

            Game.sdk.customMessage(
                Game.locale.getText(LOCALE_KEY.BOT_SCORE_BEAT_MESSAGE),
                base64Image,
                {
                    data: PayloadType.SHARE_BEAT_HIGHSCORE_LEVEL,
                    userId: userId,
                    userName: Game.sdk.getUsername(),
                    userAvatar: Game.sdk.getAvatar(),
                },
                'IMMEDIATE',
                Game.locale.getText(LOCALE_KEY.BOT_SCORE_BEAT_CTA)
            );

            gameTracking(FBTrackingEvent.ScoreBeatenShare);

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.SHARE_BEAT_HIGHSCORE_LEVEL
                )
            );
        } catch (e) {
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.SHARE_BEAT_HIGHSCORE_LEVEL,
                    e
                )
            );
        }
    }

    public static async share3StarReward(levelNumber: number) {
        const base64Image = await PayloadHelper.get3StarRewardBase64(
            levelNumber
        );
        try {
            await Game.sdk.share(
                Game.locale.getText(LOCALE_KEY.BOT_SHARE_3_STARS_MESSAGE),
                base64Image,
                { data: PayloadType.SHARE_3_STAR_REWARD },
                'REQUEST'
            );
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.SHARE_3_STAR_REWARD
                )
            );
        } catch (e) {
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.SHARE_3_STAR_REWARD,
                    e
                )
            );
        }
    }

    public static async shareScreenshot(
        message: string,
        payloadData: PayloadType
    ) {
        try {
            await Game.sdk.share(
                message,
                PayloadHelper.getScreenshotBase64(),
                { data: payloadData },
                'SHARE'
            );
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.SHARE_SCREENSHOT
                )
            );
        } catch (e) {
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.SHARE_SCREENSHOT,
                    e
                )
            );
        }
    }

    public static async sharePostcard(gameObjectId: string, worldName: string) {
        const base64Image = PayloadHelper.getGameObjectBase64(gameObjectId);

        try {
            await Game.sdk.share(
                Game.locale.getText('BotPostcardMessage'),
                base64Image,
                { data: PayloadType.SHARE_POSTCARD },
                'SHARE'
            );

            gameTracking(FBTrackingEvent.Postcard_Share_Send, 1, {
                world: worldName,
            });

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.SHARE_POSTCARD
                )
            );
        } catch (e) {
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.SHARE_POSTCARD,
                    e
                )
            );
        }
    }

    public static async loadLevelLeaderboard(levelNumber: number) {
        try {
            const entries: IScoreEntry[] = await Game.sdk.getFriendScores(
                Leaderboard.LEVEL_SCORE,
                levelNumber.toString()
            );
            Game.state.dispatch(LevelActions.setLevelLeaderboard(entries));
            await Game.sdk.levelStart(levelNumber);
        } catch (e) {
            // Ignore.
            console.info(e);
        }
    }

    public static async displayAdvertisement(
        placement: IAdvertisementPlacement,
        state: IGameState,
        payload?: IAdvertisementPayload
    ) {
        try {
            if (placement.type === AdvertisementType.REWARDED) {
                gameTracking(FBTrackingEvent.RewardedAdRequested);
            } else {
                gameTracking(FBTrackingEvent.InterstitialAdRequested);
            }

            track('placementID', placement.id);
            track('adType', placement.type.toUpperCase() as AdType);

            trackAdvertisement(state, payload);

            await Game.sdk.displayAdvertisement(placement, state, payload);

            track('adSuccess', 1);
            sendEvent(AdTrackingEventName.adRequest, state);

            if (placement.type === AdvertisementType.REWARDED) {
                gameTracking(FBTrackingEvent.RewardedAdDisplayed);
            } else {
                gameTracking(FBTrackingEvent.InterstitialAdDisplayed);
            }

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.DISPLAY_ADVERTISEMENT,
                    { placement: placement, payload: payload }
                )
            );
        } catch (e) {
            track('adSuccess', 0);
            track('adExtraCardsReceived', 0);
            track('adCoinsReceived', 0);
            sendEvent(AdTrackingEventName.adRequest, state);

            // Only track rewarded ad fails, not interstitials.
            if (placement.type === AdvertisementType.REWARDED) {
                gameTracking(FBTrackingEvent.VideoAdCannotBeDisplayedFillrate);
            }

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.DISPLAY_ADVERTISEMENT,
                    { error: e, placement: placement, payload: payload }
                )
            );
        }
    }

    public static async subscribeBotOptIn() {
        if (await Game.sdk.subscribeBot()) {
            gameTracking(FBTrackingEvent.Bot_confirm);
            GameActions.restoreContext(
                ResponseStatus.SUCCESS,
                ResponseType.SUBSCRIBE_BOT_OPT_IN
            );
        } else {
            gameTracking(FBTrackingEvent.Bot_deny);
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.SUBSCRIBE_BOT_OPT_IN
                )
            );
        }
    }

    public static async canCreateShortcut() {
        try {
            return await Game.sdk.canCreateShortcut();
        } catch (e) {
            console.error(e);
        }
    }

    public static async createShortcut() {
        try {
            await Game.sdk.createShortcut();
            gameTracking(FBTrackingEvent.CreateShortcutConfirm);
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.CREATE_SHORTCUT
                )
            );
        } catch (e) {
            if (e.code === 'USER_INPUT') {
                gameTracking(FBTrackingEvent.CreateShortcutClose);
            }
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.CREATE_SHORTCUT,
                    e
                )
            );
        }
    }

    public static async switchGame(gameId: string) {
        try {
            await Game.sdk.switchGame(gameId, 'worldMapIcon');
            // The following code is likely not executed since Facebook does not resolve switchGameAsync before exiting.
            gameTracking(FBTrackingEvent.xPromo_Confirm);
            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.SUCCESS,
                    ResponseType.SWITCH_GAME
                )
            );
        } catch (e) {
            if (
                e.code === 'USER_INPUT' &&
                e.message === 'Player closed the game switch dialog'
            ) {
                gameTracking(FBTrackingEvent.xPromo_Deny);
            }

            Game.state.dispatch(
                GameActions.restoreContext(
                    ResponseStatus.FAIL,
                    ResponseType.SWITCH_GAME,
                    e
                )
            );
        }
    }

    public static isInstallFromUpdate() {
        return Game.sdk.driver.isInstallFromUpdate();
    }

    public static hasSocialFeaturesSupport() {
        return Game.sdk.driver.hasSocialFeaturesSupport();
    }
}
